import { useState, useMemo, useRef, useEffect } from "react";
import { T } from "../theme";
import { Button, Badge } from "./fmxkit";
import DataSheet from "./DataSheet";
import { claudeFetch, parseClaudeText } from "../apiClient";
import { getBaseSchemaType, getSchemaModuleSlug } from "../schemas";
import { parseTimeToHMS } from "../fmxFieldTypes";
import {
  getActivePrompt, getEnabledExamplesForPrompt,
  createExtractionRun, completeExtractionRun, incrementExampleUsage,
} from "../db";
import { buildSystemPrompt, extractUsage } from "../promptTemplates";

// StepValidate — Validate step redesign (design_handoff_wizard_v2, Artboard A).
// Claude-checked plain language over the real validation model:
//   · cellErrors (from computeCellErrors) drive an issue taxonomy — every
//     issue has sev (error|warn) and kind (dependency|data). Dependency
//     errors are cells pointing at records that don't exist in FMX.
//   · Issues are grouped by field + value so one row fixes all N matching
//     cells ("Fix all N"); suggestions come from the project's dependency
//     caches (closest-name match) or local format heuristics.
//   · Fixes write straight into mappedRows (the source of truth) — counts,
//     the sheet, the strip, and the summary all derive and update atomically.
//   · Resolved issues stay listed (Fixed/Dismissed/Skipped) with Undo.
//   · "Write rule with Claude" bulk edits: describe → Claude writes the rule
//     → verify against a before→after preview → apply (undoable).

const SECTION_LABEL = { font: `700 11px/16px ${T.fontSans}`, textTransform: "uppercase", letterSpacing: "0.07em" };
const PILL = {
  font: `600 9.5px/14px ${T.fontSans}`, textTransform: "uppercase", letterSpacing: "0.04em",
  background: T.white, borderRadius: T.radiusPill, padding: "0 7px", flexShrink: 0,
};
const MAX_ISSUE_ROWS = 30;

// ---- closest-name matching against the FMX dependency caches -------------
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

function bestDepMatch(value, names) {
  const v = String(value).toLowerCase().trim();
  if (!v || !names?.length) return null;
  let best = null;
  let bestScore = 0;
  for (const name of names.slice(0, 2000)) {
    const n = String(name).toLowerCase().trim();
    if (!n) continue;
    let score;
    if (n === v) score = 1;
    else if (n.includes(v) || v.includes(n)) score = Math.min(n.length, v.length) / Math.max(n.length, v.length) * 0.95;
    else score = 1 - levenshtein(v, n) / Math.max(v.length, n.length);
    if (score > bestScore) { bestScore = score; best = name; }
  }
  return bestScore >= 0.55 ? { name: best, score: bestScore } : null;
}

// Try to repair a value the format validators rejected.
function suggestFormatFix(field, value) {
  if (!value) return null;
  if (field.type === "date") {
    const m = String(value).match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
    if (m) {
      let [, a, b, y] = m;
      if (y.length === 2) y = `20${y}`;
      let mo = parseInt(a, 10), d = parseInt(b, 10);
      if (mo > 12 && d <= 12) [mo, d] = [d, mo]; // day/month swap
      if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
        const iso = `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        if (!isNaN(Date.parse(iso))) {
          return { value: iso, why: `Looks like a day/month mix-up — Claude reads it as ${new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` };
        }
      }
    }
    return null;
  }
  if (field.type === "number") {
    const cleaned = String(value).replace(/[^0-9.\-]/g, "");
    if (cleaned && !isNaN(Number(cleaned)) && cleaned !== String(value)) {
      return { value: cleaned, why: "Strips the non-numeric characters — FMX needs a plain number" };
    }
  }
  if (field.type === "time") {
    const hms = parseTimeToHMS(value);
    if (hms && hms !== String(value)) {
      return { value: hms, why: `FMX stores times as 24-hour HH:MM:SS — Claude reads this as ${hms}` };
    }
  }
  if (field.type === "boolean") {
    const v = String(value).trim().toLowerCase();
    if (/^(y|t|on|enabled|x|✓)$/.test(v)) return { value: "Yes", why: "FMX needs a yes/no value — Claude reads this as Yes" };
    if (/^(n|f|off|disabled|-)$/.test(v)) return { value: "No", why: "FMX needs a yes/no value — Claude reads this as No" };
  }
  return null;
}

// ---- inline editor (issue rows) -------------------------------------------
function ValEditor({ initial, onSave, onCancel }) {
  const [v, setV] = useState(initial || "");
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <input
        autoFocus value={v} onChange={e => setV(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && v.trim()) onSave(v.trim()); if (e.key === "Escape") onCancel(); }}
        style={{ height: 26, width: 140, padding: "0 8px", border: `1px solid ${T.brand}`, borderRadius: T.radiusBtn, font: `500 12px ${T.fontData}`, color: T.ink, outline: "none", boxShadow: T.focusRing }}
      />
      <Button variant="primary" size="sm" icon="check" onClick={() => v.trim() && onSave(v.trim())}>Save</Button>
      <Button variant="neutral" size="sm" onClick={onCancel}>Cancel</Button>
    </span>
  );
}

export default function StepValidate({
  mappedHeaders,
  mappedRows,
  setMappedRows,
  cellErrors,
  allFields,
  schemaType,
  depCacheMap,
  depAutoSyncing,
  projectId,
  userId,
  fileInfo,
  siteUrl,
  skippedRows,
  setSkippedRows,
  onRecheck,
}) {
  // Cross-highlight (issue group key), scroll/sticky, in-sheet editing.
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [problemOnly, setProblemOnly] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [cellEdits, setCellEdits] = useState({}); // "ri-field" -> true (clean cells edited in place)
  const [dismissed, setDismissed] = useState({}); // groupKey -> true
  // Resolved-issue log so Fixed/Skipped rows stay listed with Undo.
  const [resolvedLog, setResolvedLog] = useState([]);
  const [editingIssue, setEditingIssue] = useState(null);
  // Bulk rules written with Claude.
  const [bulkDraft, setBulkDraft] = useState(null); // description string -> opens the modal
  const [bulkRules, setBulkRules] = useState([]);   // [{ text, field, count, snapshot }]
  const [scrolled, setScrolled] = useState(false);
  const [stickyPad, setStickyPad] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => {
    let el = rootRef.current?.parentElement;
    while (el && el !== document.body) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      el = el.parentElement;
    }
    if (!el || el === document.body) return;
    setStickyPad(parseFloat(getComputedStyle(el).paddingTop) || 0);
    const onScroll = () => setScrolled(el.scrollTop > 160);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const fieldsByName = useMemo(() => {
    const m = {};
    allFields.forEach(f => { m[f.name] = f; });
    return m;
  }, [allFields]);

  const moduleSlug = schemaType ? getSchemaModuleSlug(schemaType) : null;
  const depListFor = f => (moduleSlug && depCacheMap?.[`${f.crossSheet}:${moduleSlug}`]) || depCacheMap?.[f.crossSheet] || null;
  const site = siteUrl || "cached FMX data";
  const totalRows = mappedRows.length;
  const skipped = skippedRows || new Set();

  // ---- issue derivation: group flagged cells by field + value -------------
  const { issues, issueByCell } = useMemo(() => {
    const groups = new Map();
    for (const [key, kindRaw] of Object.entries(cellErrors)) {
      const dash = key.indexOf("-");
      const ri = parseInt(key.slice(0, dash), 10);
      const fieldName = key.slice(dash + 1);
      if (isNaN(ri) || skipped.has(ri)) continue;
      const f = fieldsByName[fieldName];
      if (!f) continue;
      const val = String(mappedRows[ri]?.[fieldName] ?? "");
      const sev = kindRaw === "warning" ? "warn" : "error";
      const kind = kindRaw === "dep_error" ? "dependency" : "data";
      const gKey = `${kind}|${fieldName}|${val}`;
      let g = groups.get(gKey);
      if (!g) {
        g = { id: gKey, sev, kind, field: fieldName, val, rows: [] };
        groups.set(gKey, g);
      }
      g.rows.push(ri);
    }
    const list = [...groups.values()];
    // Enrich with plain-language message + Claude-style suggestion.
    for (const g of list) {
      const f = fieldsByName[g.field];
      if (g.kind === "dependency") {
        const names = depListFor(f);
        const match = bestDepMatch(g.val, names);
        g.msg = `${f.crossSheet} not found in FMX`;
        if (match) {
          g.suggest = match.name;
          g.why = `${Math.round(match.score * 100)}% match to "${match.name}" on ${site} — appears in ${g.rows.length} row${g.rows.length === 1 ? "" : "s"}`;
        } else {
          g.suggest = null;
          g.why = `No close match among the ${names?.length ?? 0} FMX ${f.crossSheet} records — match it manually, or import that entity first`;
        }
      } else if (g.sev === "warn") {
        g.msg = `${g.field} is longer than the FMX limit`;
        g.suggest = null;
        g.why = "Push is allowed — FMX may truncate the value";
      } else if (!g.val) {
        g.msg = `${g.field} is empty`;
        g.suggest = null;
        g.why = `Required by FMX — fill it in${g.rows.length > 1 ? ` (${g.rows.length} rows)` : ""} or skip the row`;
      } else {
        const typeLabel = { date: "date", number: "number", email: "email address", time: "time", boolean: "yes/no value" }[f.type] || "value";
        g.msg = `Not a valid ${typeLabel}`;
        const fix = suggestFormatFix(f, g.val);
        g.suggest = fix?.value || null;
        g.why = fix?.why || `FMX expects a ${typeLabel} here`;
      }
    }
    // Stable order: errors before warnings, then by first row.
    list.sort((a, b) => (a.sev === b.sev ? a.rows[0] - b.rows[0] : a.sev === "error" ? -1 : 1));
    const byCell = {};
    list.forEach(g => g.rows.forEach(ri => { byCell[`${ri}-${g.field}`] = g; }));
    return { issues: list, issueByCell: byCell };
  }, [cellErrors, mappedRows, fieldsByName, depCacheMap, moduleSlug, skipped, site]);

  const openIssues = issues.filter(g => !dismissed[g.id]);
  const errGroups = openIssues.filter(g => g.sev === "error");
  const warnGroups = openIssues.filter(g => g.sev === "warn");
  const depLeft = errGroups.filter(g => g.kind === "dependency").length;
  const dataErrLeft = errGroups.filter(g => g.kind === "data").length;
  const errLeft = errGroups.length;
  const warnLeft = warnGroups.length;

  const errorRowSet = useMemo(() => {
    const s = new Set();
    errGroups.forEach(g => g.rows.forEach(ri => s.add(ri)));
    return s;
  }, [errGroups]);
  const ready = totalRows - errorRowSet.size - [...skipped].filter(ri => !errorRowSet.has(ri)).length;

  const isActive = id => !!id && (hover === id || pinned === id);
  const togglePin = id => id && setPinned(p => (p === id ? null : id));

  // ---- resolution actions (write straight into mappedRows) ----------------
  const applyValues = (rows, field, value) => {
    setMappedRows(prev => {
      const next = [...prev];
      rows.forEach(ri => { if (next[ri]) next[ri] = { ...next[ri], [field]: value }; });
      return next;
    });
  };

  const resolveIssue = (g, value) => {
    const prevRows = g.rows.map(ri => ({ ri, prev: String(mappedRows[ri]?.[g.field] ?? "") }));
    applyValues(g.rows, g.field, value);
    setResolvedLog(log => [...log, { id: `${g.id}@${Date.now()}`, type: "fixed", sev: g.sev, kind: g.kind, field: g.field, val: g.val, value, rows: prevRows }]);
    setPinned(null);
  };

  const dismissIssue = g => {
    setDismissed(d => ({ ...d, [g.id]: true }));
  };

  const skipIssueRows = g => {
    setSkippedRows(prev => {
      const next = new Set(prev);
      g.rows.forEach(ri => next.add(ri));
      return next;
    });
    setResolvedLog(log => [...log, { id: `${g.id}@${Date.now()}`, type: "skipped", sev: g.sev, kind: g.kind, field: g.field, val: g.val, rows: g.rows.map(ri => ({ ri })) }]);
  };

  const undoLogEntry = entry => {
    if (entry.type === "fixed") {
      setMappedRows(prev => {
        const next = [...prev];
        entry.rows.forEach(({ ri, prev: p }) => { if (next[ri]) next[ri] = { ...next[ri], [entry.field]: p }; });
        return next;
      });
    } else if (entry.type === "skipped") {
      setSkippedRows(prev => {
        const next = new Set(prev);
        entry.rows.forEach(({ ri }) => next.delete(ri));
        return next;
      });
    }
    setResolvedLog(log => log.filter(e => e !== entry));
  };

  // Fixed-cell wash for cells restored by the log (issue no longer flags them).
  const fixedCellMap = useMemo(() => {
    const m = {};
    resolvedLog.forEach(e => { if (e.type === "fixed") e.rows.forEach(({ ri }) => { m[`${ri}-${e.field}`] = e.value; }); });
    return m;
  }, [resolvedLog]);

  // ---- in-sheet click-to-edit ---------------------------------------------
  const saveCell = (ri, field, value) => {
    setEditingKey(null);
    const current = String(mappedRows[ri]?.[field] ?? "");
    if (value === undefined || value === current) return;
    const g = issueByCell[`${ri}-${field}`];
    if (g) {
      const prevRows = [{ ri, prev: current }];
      applyValues([ri], field, value);
      setResolvedLog(log => [...log, { id: `${g.id}@${ri}@${Date.now()}`, type: "fixed", sev: g.sev, kind: g.kind, field, val: current, value, rows: prevRows }]);
    } else {
      applyValues([ri], field, value);
      setCellEdits(c => ({ ...c, [`${ri}-${field}`]: true }));
    }
  };

  // ---- bulk "Write rule with Claude" ---------------------------------------
  const [chipSuggestions, setChipSuggestions] = useState([]);
  useEffect(() => {
    // Seed suggestion chips from the live issues (top fixable patterns).
    const chips = [];
    for (const g of issues) {
      if (g.kind === "dependency" && g.suggest) chips.push(`Replace "${g.val}" with "${g.suggest}" in ${g.field}`);
      if (chips.length >= 2) break;
    }
    chips.push("Trim whitespace from every cell");
    setChipSuggestions(chips.slice(0, 3));
  }, [issues.length]);

  const applyBulkRule = rule => {
    // rule: { text, field, fn, count }
    const snapshot = mappedRows.map(r => ({ ...r }));
    setMappedRows(prev => prev.map(row => {
      try {
        const v = rule.fn(row);
        if (v === null || v === undefined) return row;
        const sv = String(v);
        if (sv === String(row[rule.field] ?? "")) return row;
        return { ...row, [rule.field]: sv };
      } catch { return row; }
    }));
    setBulkRules(r => [...r, { text: rule.text, field: rule.field, count: rule.count, snapshot }]);
    setBulkDraft(null);
  };

  const undoBulkRule = k => {
    const rule = bulkRules[k];
    if (rule?.snapshot) setMappedRows(rule.snapshot);
    setBulkRules(r => r.filter((_, i) => i !== k));
  };

  // ---- pieces ---------------------------------------------------------------
  const issueRow = (g, i, last) => {
    const on = isActive(g.id);
    const isDismissed = !!dismissed[g.id];
    const err = g.sev === "error";
    const accent = err ? T.red500 : T.orange500;
    const editing = editingIssue === g.id;
    const label = g.rows.length === 1 ? `Row ${g.rows[0] + 1} · ${g.field}` : `${g.rows.length} rows · ${g.field}`;
    return (
      <div
        key={g.id}
        onMouseEnter={() => setHover(g.id)}
        onMouseLeave={() => setHover(null)}
        onClick={() => togglePin(g.id)}
        style={{
          borderBottom: last ? "none" : `1px solid ${T.borderSubtle}`,
          padding: "9px 14px",
          background: on ? T.blue50 : isDismissed ? T.bg : err ? T.tint.error : T.tint.warning,
          boxShadow: on ? `inset 2px 0 0 ${T.brand}` : `inset 2px 0 0 ${isDismissed ? T.border : accent}`,
          cursor: "pointer", transition: "background 120ms ease-out",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "15px 170px 1fr auto", alignItems: "center", gap: 10 }}>
          <i
            className={`fa-solid fa-${isDismissed ? "check" : err ? (g.kind === "dependency" ? "link-slash" : "circle-exclamation") : "triangle-exclamation"}`}
            style={{ fontSize: 12, color: isDismissed ? T.inkSoft : accent }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ font: `600 13px/18px ${T.fontSans}`, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
            <div style={{ marginTop: 2 }}>
              <span style={{ font: `500 11px/16px ${T.fontData}`, color: isDismissed ? T.inkSoft : T.ink, background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: 3, padding: "1px 5px" }}>{g.val || "—"}</span>
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ font: `600 12.5px/17px ${T.fontSans}`, color: T.ink, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {!isDismissed && (err
                ? <span style={{ ...PILL, color: T.redDarker, border: `1px solid ${T.red500}` }}>{g.kind === "dependency" ? "Dependency" : "Blocks push"}</span>
                : <span style={{ ...PILL, color: T.orangeText, border: `1px solid ${T.orange500}` }}>Warning</span>)}
              <span>{isDismissed ? "Dismissed — will push as-is" : g.msg}</span>
            </div>
            {!isDismissed && (
              <div style={{ font: `400 11.5px/15px ${T.fontSans}`, color: T.inkMuted, marginTop: 1 }}>
                {g.suggest
                  ? <span><i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 9, color: T.brand, marginRight: 5 }} />Claude suggests <b style={{ color: T.ink, fontFamily: T.fontData, fontWeight: 600 }}>{g.suggest}</b> — {g.why}</span>
                  : g.why}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            {isDismissed
              ? <Button variant="neutral" size="sm" icon="rotate-left" onClick={() => setDismissed(d => ({ ...d, [g.id]: false }))}>Undo</Button>
              : editing
                ? <ValEditor initial={g.suggest || g.val} onSave={v => { resolveIssue(g, v); setEditingIssue(null); }} onCancel={() => setEditingIssue(null)} />
                : (
                  <>
                    {g.suggest && (
                      <Button variant="primary" size="sm" icon="wand-magic-sparkles" onClick={() => resolveIssue(g, g.suggest)}>
                        {g.rows.length > 1 ? `Fix all ${g.rows.length}` : "Apply fix"}
                      </Button>
                    )}
                    <Button variant="neutral" size="sm" icon="pencil" onClick={() => setEditingIssue(g.id)}>Edit</Button>
                    {err
                      ? <Button variant="neutral" size="sm" icon="ban" onClick={() => skipIssueRows(g)}>{g.rows.length > 1 ? "Skip rows" : "Skip row"}</Button>
                      : <Button variant="neutral" size="sm" icon="check" onClick={() => dismissIssue(g)}>Dismiss</Button>}
                  </>
                )}
          </div>
        </div>
      </div>
    );
  };

  const resolvedRow = (e, last) => (
    <div key={e.id} style={{ borderBottom: last ? "none" : `1px solid ${T.borderSubtle}`, padding: "9px 14px", background: e.type === "fixed" ? T.white : T.bg, boxShadow: `inset 2px 0 0 ${e.type === "fixed" ? T.green500 : T.border}` }}>
      <div style={{ display: "grid", gridTemplateColumns: "15px 170px 1fr auto", alignItems: "center", gap: 10 }}>
        <i className={`fa-solid fa-${e.type === "fixed" ? "circle-check" : "ban"}`} style={{ fontSize: 12, color: e.type === "fixed" ? T.green500 : T.inkSoft }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ font: `600 13px/18px ${T.fontSans}`, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {e.rows.length === 1 ? `Row ${e.rows[0].ri + 1} · ${e.field}` : `${e.rows.length} rows · ${e.field}`}
          </div>
          <div style={{ marginTop: 2 }}>
            <span style={{ font: `500 11px/16px ${T.fontData}`, color: T.inkSoft, background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: 3, padding: "1px 5px", textDecoration: e.type === "fixed" ? "line-through" : "none" }}>{e.val || "—"}</span>
            {e.type === "fixed" && <span style={{ font: `500 11px/16px ${T.fontData}`, color: T.greenDark, marginLeft: 6 }}>→ {e.value}</span>}
          </div>
        </div>
        <div style={{ font: `600 12.5px/17px ${T.fontSans}`, color: e.type === "fixed" ? T.greenDark : T.inkMuted }}>
          {e.type === "fixed" ? `Fixed — ${e.field} is now "${e.value}"` : `Skipped — ${e.rows.length === 1 ? "this row won't be pushed" : "these rows won't be pushed"}`}
        </div>
        <div style={{ flexShrink: 0 }}>
          <Button variant="neutral" size="sm" icon="rotate-left" onClick={() => undoLogEntry(e)}>Undo</Button>
        </div>
      </div>
    </div>
  );

  const depOpen = openIssues.filter(g => g.kind === "dependency");
  const dataOpen = openIssues.filter(g => g.kind === "data");
  const depResolved = resolvedLog.filter(e => e.kind === "dependency");
  const dataResolved = resolvedLog.filter(e => e.kind === "data");
  const dismissedData = issues.filter(g => g.kind === "data" && dismissed[g.id]);

  const problemRowIndices = useMemo(() => {
    const s = new Set();
    openIssues.forEach(g => g.rows.forEach(ri => s.add(ri)));
    return [...s].sort((a, b) => a - b);
  }, [openIssues]);

  const renderSection = (labelNode, helper, border, open, resolved, dismissedRows = []) => {
    const visible = open.slice(0, MAX_ISSUE_ROWS);
    const items = [...visible.map((g, i) => ({ g })), ...dismissedRows.map(g => ({ g, dismissed: true })), ...resolved.map(e => ({ e }))];
    return (
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
          {labelNode}
          <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkMuted }}>{helper}</span>
        </div>
        <div style={{ background: T.white, border: `1px solid ${border}`, borderRadius: T.radiusTable, overflow: "hidden" }}>
          {items.length === 0 && (
            <div style={{ padding: 14, font: `500 13px ${T.fontSans}`, color: T.greenDark, display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa-solid fa-circle-check" />Nothing to fix here.
            </div>
          )}
          {items.map((it, i) => {
            const last = i === items.length - 1 && open.length <= MAX_ISSUE_ROWS;
            if (it.e) return resolvedRow(it.e, last);
            return issueRow(it.g, i, last);
          })}
          {open.length > MAX_ISSUE_ROWS && (
            <div style={{ padding: "8px 14px", font: `400 12px ${T.fontSans}`, color: T.inkSoft }}>
              +{open.length - MAX_ISSUE_ROWS} more — fix the ones above or use a bulk rule.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={rootRef} style={{ width: "100%", maxWidth: 1280, margin: "0 auto" }}>
      {/* Status strip */}
      <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <span style={{ width: 34, height: 34, borderRadius: T.radiusPill, background: T.brand, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <i className="fa-solid fa-list-check" style={{ fontSize: 14 }} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: `700 15px/20px ${T.fontSans}`, color: T.ink }}>
            Claude checked all {totalRows} rows against {siteUrl ? `${siteUrl}` : "your cached FMX data"}.
          </div>
          <div style={{ font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted, marginTop: 2 }}>
            <b style={{ color: T.red500 }}>Red</b> cells block the push — fix or skip those rows. <b style={{ color: T.orangeText }}>Orange</b> are warnings; you can push through them.
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ display: "flex", height: 8, borderRadius: T.radiusPill, overflow: "hidden", width: 220 }}>
            <div style={{ flex: 1, background: T.green500 }} />
            {errLeft > 0 && <div style={{ width: Math.min(80, errLeft * 14), background: T.red500, marginLeft: 2, flexShrink: 0 }} />}
            {warnLeft > 0 && <div style={{ width: Math.min(60, warnLeft * 14), background: T.orange500, marginLeft: 2, flexShrink: 0 }} />}
          </div>
          <div style={{ font: `500 12px/16px ${T.fontSans}`, color: T.inkMuted, marginTop: 6, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.green500, marginRight: 5 }} />{ready} ready</span>
            {depLeft > 0 && <span><i className="fa-solid fa-link-slash" style={{ fontSize: 9, color: T.red500, marginRight: 5 }} />{depLeft} dependency</span>}
            {dataErrLeft > 0 && <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.red500, marginRight: 5 }} />{dataErrLeft} data</span>}
            {warnLeft > 0 && <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.orange500, marginRight: 5 }} />{warnLeft} warning{warnLeft === 1 ? "" : "s"}</span>}
            {errLeft === 0 && warnLeft === 0 && <span style={{ color: T.greenDark }}><i className="fa-solid fa-circle-check" style={{ marginRight: 5 }} />all clear</span>}
          </div>
        </div>
      </div>

      {/* Sticky sheet */}
      <div style={{ position: "sticky", top: 48 - stickyPad, zIndex: 20, marginBottom: 14 }}>
        <DataSheet
          title={fileInfo?.name || `${schemaType} data`}
          meta={`checked against ${site}${depAutoSyncing ? " · refreshing…" : ""}`}
          hint="Hover a flagged cell to find its fix below"
          totalRows={totalRows}
          matchRow={(i, ql) => mappedHeaders.some(h => String(mappedRows[i]?.[h] ?? "").toLowerCase().includes(ql))}
          defaultBodyHeight={318}
          minBodyHeight={116}
          searchInHeader={false}
          elevated={scrolled}
          visibleOverride={problemOnly ? problemRowIndices : null}
          onPageSizeSelect={() => problemOnly && setProblemOnly(false)}
          toolbar={({ searchBox }) => (
            <>
              {searchBox}
              <div style={{ flex: 1 }} />
              {depLeft > 0 && <Badge color="red" icon="link-slash">{`${depLeft} dependency`}</Badge>}
              {dataErrLeft > 0 && <Badge color="red" icon="circle-exclamation">{`${dataErrLeft} data error${dataErrLeft === 1 ? "" : "s"}`}</Badge>}
              {warnLeft > 0 && <Badge color="orange" icon="triangle-exclamation">{`${warnLeft} warning${warnLeft === 1 ? "" : "s"}`}</Badge>}
              {errLeft === 0 && warnLeft === 0 && <Badge color="green" icon="circle-check">All clear</Badge>}
              <label onClick={() => setProblemOnly(p => !p)} style={{ font: `500 13px ${T.fontSans}`, color: T.ink, display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
                <span style={{ width: 16, height: 16, border: `1.5px solid ${problemOnly ? T.brand : T.borderStrong}`, borderRadius: 3, background: problemOnly ? T.brand : T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  {problemOnly && <i className="fa-solid fa-check" style={{ fontSize: 10, color: T.white }} />}
                </span>
                Problem rows only
              </label>
            </>
          )}
        >
          {({ rowIndices, query }) => (
            <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", tableLayout: "fixed", minWidth: Math.max(640, mappedHeaders.length * 110 + 38) }}>
              <thead>
                <tr>
                  <th style={{ width: 38, padding: "6px 8px", textAlign: "center", font: `700 11px/14px ${T.fontSans}`, color: T.inkSoft, background: T.bgMuted, borderBottom: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}`, position: "sticky", top: 0, zIndex: 2 }}>#</th>
                  {mappedHeaders.map((h, j) => (
                    <th key={h} style={{ padding: "6px 10px", textAlign: "left", font: `700 11px/14px ${T.fontSans}`, color: T.inkMuted, textTransform: "uppercase", letterSpacing: "0.04em", background: T.bgMuted, borderBottom: `1px solid ${T.border}`, borderRight: j === mappedHeaders.length - 1 ? "none" : `1px solid ${T.border}`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", position: "sticky", top: 0, zIndex: 2 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowIndices.map(ri => {
                  const isSkippedRow = skipped.has(ri);
                  return (
                    <tr key={ri} style={{ opacity: isSkippedRow ? 0.5 : 1 }}>
                      <td title={isSkippedRow ? "Skipped — won't be pushed" : undefined} style={{ padding: "6px 8px", textAlign: "center", font: `400 11px/16px ${T.fontData}`, color: T.inkSoft, background: T.bgMuted, borderBottom: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>
                        {isSkippedRow ? <i className="fa-solid fa-ban" style={{ fontSize: 9 }} /> : ri + 1}
                      </td>
                      {mappedHeaders.map((h, j) => {
                        const key = `${ri}-${h}`;
                        const g = issueByCell[key];
                        const v = String(mappedRows[ri]?.[h] ?? "");
                        const hit = !!query && v.toLowerCase().includes(query);
                        const base = {
                          padding: "6px 10px", borderBottom: `1px solid ${T.border}`,
                          borderRight: j === mappedHeaders.length - 1 ? "none" : `1px solid ${T.border}`,
                          font: `400 12px/16px ${T.fontData}`, color: T.inkMuted,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          background: T.white, cursor: "text",
                        };
                        if (editingKey === key) {
                          return (
                            <td key={j} style={{ ...base, padding: 0, boxShadow: `inset 0 0 0 2px ${T.brand}` }}>
                              <input
                                autoFocus
                                defaultValue={v}
                                onBlur={e => saveCell(ri, h, e.target.value.trim())}
                                onKeyDown={e => { if (e.key === "Enter") saveCell(ri, h, e.target.value.trim()); if (e.key === "Escape") setEditingKey(null); }}
                                style={{ width: "100%", border: "none", outline: "none", padding: "6px 10px", font: `500 12px/16px ${T.fontData}`, color: T.ink, background: "transparent", boxSizing: "border-box" }}
                              />
                            </td>
                          );
                        }
                        if (!g) {
                          const fixedVal = fixedCellMap[key];
                          const edited = cellEdits[key];
                          return (
                            <td
                              key={j}
                              onClick={() => setEditingKey(key)}
                              title="Click to edit"
                              style={{
                                ...base,
                                background: fixedVal !== undefined ? T.tint.success : edited ? T.tint.info : hit ? T.tint.warning : T.white,
                                boxShadow: fixedVal !== undefined ? `inset 0 -2px 0 ${T.green500}` : "none",
                                color: fixedVal !== undefined ? T.greenDark : edited || hit ? T.ink : T.inkMuted,
                                fontWeight: hit ? 600 : 400,
                              }}
                            >
                              {v || <span style={{ color: T.border }}>—</span>}
                              {fixedVal !== undefined && (
                                <div style={{ font: `400 10.5px/14px ${T.fontSans}`, color: T.greenDark }}>
                                  <i className="fa-solid fa-circle-check" style={{ fontSize: 9, marginRight: 4 }} />Fixed
                                </div>
                              )}
                            </td>
                          );
                        }
                        const on = isActive(g.id);
                        const err = g.sev === "error";
                        const accent = err ? T.red500 : T.orange500;
                        const isDismissed = !!dismissed[g.id];
                        // Chosen tweak: flagged cells use the OUTLINE style (severity ring on white).
                        const style = { ...base, cursor: "pointer" };
                        if (isDismissed) { /* plain white */ }
                        else style.boxShadow = `inset 0 0 0 2px ${accent}`;
                        if (on) { style.boxShadow = `inset 0 0 0 2px ${T.brand}`; style.background = T.blue50; }
                        return (
                          <td
                            key={j}
                            onMouseEnter={() => setHover(g.id)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => { setEditingKey(key); setPinned(g.id); }}
                            style={style}
                          >
                            <div style={{ color: isDismissed ? T.inkMuted : err ? T.redDarker : T.orangeText, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}>{v || "—"}</div>
                            <div style={{ font: `400 10.5px/14px ${T.fontSans}`, color: isDismissed ? T.inkSoft : err ? T.red500 : T.orange500, overflow: "hidden", textOverflow: "ellipsis" }}>
                              {isDismissed
                                ? <span><i className="fa-solid fa-check" style={{ fontSize: 9, marginRight: 4 }} />Dismissed</span>
                                : <span><i className={`fa-solid fa-${err ? (g.kind === "dependency" ? "link-slash" : "circle-exclamation") : "triangle-exclamation"}`} style={{ fontSize: 9, marginRight: 4 }} />{g.msg}</span>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </DataSheet>
      </div>

      {/* Fix list */}
      <div style={{ maxWidth: 980, width: "100%", display: "flex", flexDirection: "column", gap: 14, margin: "4px auto 0" }}>
        <BulkRuleCard
          chips={chipSuggestions}
          rules={bulkRules}
          onStart={d => setBulkDraft(d)}
          onUndo={undoBulkRule}
        />

        {renderSection(
          <span style={{ ...SECTION_LABEL, color: depOpen.length ? T.redDarker : T.greenDark }}>
            <i className="fa-solid fa-link-slash" style={{ fontSize: 10, marginRight: 5 }} />Dependency errors
          </span>,
          "— these cells point at records that don't exist in FMX yet; match them to an existing record, or import that entity first",
          depOpen.length ? T.red500 : T.green500,
          depOpen, depResolved,
        )}

        {renderSection(
          <span style={{ ...SECTION_LABEL, color: T.orangeText }}>Missing data & formats</span>,
          "— empty values and incorrect formats; errors block the push, warnings never do",
          T.border,
          dataOpen.filter(g => !dismissed[g.id]), dataResolved, dismissedData,
        )}

        {/* Summary */}
        <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, font: `600 13px ${T.fontSans}`, color: errLeft === 0 ? T.green500 : T.red500 }}>
            <i className={`fa-solid fa-${errLeft === 0 ? "circle-check" : "circle-exclamation"}`} />
            {errLeft === 0
              ? `All ${ready} rows are ready to push`
              : `${errLeft} error${errLeft === 1 ? "" : "s"} block${errLeft === 1 ? "s" : ""} the push`}
          </div>
          <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, marginTop: 6 }}>
            {errLeft === 0
              ? (warnLeft > 0 ? `${warnLeft} warning${warnLeft === 1 ? "" : "s"} remain — you can push through warnings.` : "No errors or warnings left. Continue to push.")
              : "Fix the red rows above (or skip them) — warnings never block the push."}
            {skipped.size > 0 && ` ${skipped.size} skipped row${skipped.size === 1 ? "" : "s"} stay${skipped.size === 1 ? "s" : ""} in your file and won't be pushed.`}
          </div>
          {onRecheck && (
            <Button variant="secondary" size="sm" icon="rotate" disabled={depAutoSyncing} onClick={onRecheck} style={{ marginTop: 12, width: "100%", justifyContent: "center" }}>
              {depAutoSyncing ? "Re-checking against FMX…" : "Re-check against FMX"}
            </Button>
          )}
        </div>
      </div>

      {bulkDraft != null && (
        <BulkRuleModal
          description={bulkDraft}
          mappedHeaders={mappedHeaders}
          mappedRows={mappedRows}
          schemaType={schemaType}
          projectId={projectId}
          userId={userId}
          sourceFilename={fileInfo?.name || null}
          onApply={applyBulkRule}
          onClose={() => setBulkDraft(null)}
        />
      )}
    </div>
  );
}

// ---- bulk-edit card (gradient, input + chips + applied rules) -------------
function BulkRuleCard({ chips, rules, onStart, onUndo }) {
  const [desc, setDesc] = useState("");
  const start = d => { if (d && d.trim()) { onStart(d.trim()); setDesc(""); } };
  return (
    <div style={{ background: "linear-gradient(160deg, #eaf6f9, #fff 70%)", border: `1px solid ${T.blue200}`, borderRadius: T.radiusCard, padding: "12px 14px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ width: 30, height: 30, borderRadius: T.radiusPill, background: T.brand, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 13 }} />
        </span>
        <div style={{ position: "relative", flex: "1 1 220px" }}>
          <input
            value={desc}
            onChange={e => setDesc(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") start(desc); }}
            placeholder='Describe a bulk edit… e.g. "Replace Bldg 12 with Building 12 everywhere"'
            style={{ width: "100%", height: 34, padding: "0 12px", border: `1px solid ${T.borderStrong}`, borderRadius: T.radiusBtn, font: `400 14px ${T.fontSans}`, color: T.ink, outline: "none", boxSizing: "border-box", background: T.white }}
          />
        </div>
        <Button variant="primary" size="sm" icon="wand-magic-sparkles" onClick={() => start(desc)} style={desc.trim() ? {} : { opacity: 0.45 }}>
          Write rule with Claude
        </Button>
      </div>
      {chips.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", paddingLeft: 40 }}>
          {chips.map(s => (
            <span key={s} onClick={() => start(s)} style={{ font: `500 12px ${T.fontSans}`, color: T.brand, background: T.white, border: `1px solid ${T.blue200}`, borderRadius: T.radiusPill, padding: "3px 10px", cursor: "pointer" }}>{s}</span>
          ))}
        </div>
      )}
      {rules.length > 0 && (
        <div style={{ marginTop: 10, paddingLeft: 40, display: "flex", flexDirection: "column", gap: 6 }}>
          {rules.map((r, k) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusBtn, padding: "5px 10px" }}>
              <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 10, color: T.brand, flexShrink: 0 }} />
              <span style={{ flex: 1, font: `400 12px/16px ${T.fontSans}`, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r.text}>{r.text}</span>
              <span style={{ font: `500 10px/15px ${T.fontSans}`, color: T.greenDark, background: T.tint.success, border: `1px solid ${T.green500}`, borderRadius: T.radiusPill, padding: "0 7px", flexShrink: 0 }}>Applied{r.count ? ` · ${r.count} rows` : ""}</span>
              <Button variant="neutral" size="sm" icon="rotate-left" onClick={() => onUndo(k)} style={{ flexShrink: 0 }}>Undo</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- "Write rule with Claude" modal: writing → verify → apply -------------
const BULK_CONTRACT =
  'Return ONLY a JSON object, no markdown: {"rule": "<one or two plain-language sentences stating exactly what the rule does and roughly how many rows it touches — the user verifies this>", ' +
  '"field": "<the single column the rule writes to — must be one of the provided column names>", ' +
  '"code": "<JavaScript function body (no declaration) that receives `row` (object keyed by column name) and returns the new string value for that column, or null to leave the row unchanged>"}';

function BulkRuleModal({ description, mappedHeaders, mappedRows, schemaType, projectId, userId, sourceFilename, onApply, onClose }) {
  const [step, setStep] = useState("writing");
  const [ruleText, setRuleText] = useState("");
  const [field, setField] = useState(null);
  const [fnRef, setFnRef] = useState(null);
  const [preview, setPreview] = useState([]);
  const [count, setCount] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      const baseType = getBaseSchemaType(schemaType);
      const start = Date.now();
      let promptRow = null;
      let examples = [];
      let runId = null;
      try {
        promptRow = await getActivePrompt(baseType, "bulk_edit");
        if (promptRow?.id) {
          examples = await getEnabledExamplesForPrompt(promptRow.id);
          const runRow = await createExtractionRun({
            projectId, userId, migrationType: baseType, stage: "bulk_edit",
            sourceFilename, pageCount: null, promptId: promptRow.id, promptVersion: promptRow.version,
          });
          runId = runRow?.id || null;
        }
      } catch { /* fall back to inline prompt */ }

      const samples = mappedRows.slice(0, 5);
      const facts =
        `Columns: ${JSON.stringify(mappedHeaders)}.\nSample rows: ${JSON.stringify(samples)}.\nTotal rows: ${mappedRows.length}.\n` +
        `User's description of the bulk edit: "${description}"`;
      const system = promptRow?.body
        ? buildSystemPrompt({
            body: promptRow.body,
            vars: { MIGRATION_TYPE: baseType, COLUMNS: mappedHeaders, SAMPLE_ROWS: samples, INSTRUCTION: description },
            examples,
          })
        : `You write bulk-edit rules for an FMX data-migration tool's Validate step. The user describes an edit in plain language; you formalize it. ${BULK_CONTRACT}`;

      try {
        const res = await claudeFetch({
          max_tokens: 800,
          system,
          messages: [{ role: "user", content: `${facts}\n\n${BULK_CONTRACT}` }],
        });
        const clean = parseClaudeText(res) || "{}";
        let parsed = null;
        try { parsed = JSON.parse(clean); } catch { /* handled below */ }
        if (!parsed?.code || !parsed?.field || !mappedHeaders.includes(parsed.field)) {
          throw new Error("Claude returned an unexpected response — try rephrasing the edit.");
        }
        const fn = new Function("row", `"use strict"; ${parsed.code}`);
        // Test against all rows, building the before→after preview.
        const prev = [];
        let n = 0;
        for (const row of mappedRows) {
          let v;
          try { v = fn(row); } catch { v = null; }
          if (v === null || v === undefined) continue;
          const before = String(row[parsed.field] ?? "");
          const after = String(v);
          if (after !== before) {
            n++;
            if (prev.length < 3) prev.push([before, after]);
          }
        }
        if (!alive) return;
        setRuleText(parsed.rule || description);
        setField(parsed.field);
        setFnRef(() => fn);
        setPreview(prev.length ? prev : [["(no rows affected)", "(no change)"]]);
        setCount(n);
        setStep("verify");
        if (runId) {
          const usage = extractUsage(res);
          completeExtractionRun(runId, {
            status: "complete",
            resultJson: { description, rule: parsed.rule, field: parsed.field, code: parsed.code, affected: n },
            durationMs: Date.now() - start,
            inputTokens: usage.inputTokens, outputTokens: usage.outputTokens, estimatedCostUsd: usage.costUsd,
          });
          if (examples?.length) incrementExampleUsage(examples.map(e => e.id));
        }
      } catch (e) {
        if (runId) completeExtractionRun(runId, { status: "error", error: e?.message || String(e), durationMs: Date.now() - start });
        if (!alive) return;
        setErr(e?.status ? e.message : (e?.message || "Rule generation failed — check your connection."));
        setStep("error");
      }
    })();
    return () => { alive = false; };
  }, []);

  const label = { font: `700 11px/16px ${T.fontSans}`, textTransform: "uppercase", letterSpacing: "0.07em", color: T.inkSoft };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "120px 20px 20px", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 560, maxWidth: "100%", background: T.white, borderRadius: T.radiusCard, boxShadow: T.shadowLg, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.borderSubtle}`, display: "flex", alignItems: "center", gap: 10 }}>
          <i className="fa-solid fa-wand-magic-sparkles" style={{ color: T.brand, fontSize: 14 }} />
          <span style={{ font: `700 16px ${T.fontSans}`, color: T.ink }}>Write rule with Claude</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: T.inkMuted, fontSize: 15, padding: 4 }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {step === "writing" && (
          <div style={{ padding: "36px 18px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 22, color: T.brand }} />
            <div style={{ font: `600 14px ${T.fontSans}`, color: T.ink }}>Claude is writing the rule…</div>
            <div style={{ font: `400 12px ${T.fontSans}`, color: T.inkMuted }}>Testing it against all {mappedRows.length} rows</div>
          </div>
        )}

        {step === "error" && (
          <div style={{ padding: 18 }}>
            <div style={{ font: `400 13px/18px ${T.fontSans}`, color: T.red500 }}>{err}</div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <Button variant="neutral" onClick={onClose}>Close</Button>
            </div>
          </div>
        )}

        {step === "verify" && (
          <>
            <div style={{ padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ width: 22, height: 22, borderRadius: T.radiusPill, background: T.brand, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 10 }} />
                </span>
                <span style={{ font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted }}>
                  Claude wrote this rule — <b style={{ color: T.ink }}>check it does what you expect</b>, and edit it if not.
                </span>
              </div>
              <textarea
                value={ruleText}
                onChange={e => setRuleText(e.target.value)}
                rows={3}
                style={{ width: "100%", boxSizing: "border-box", resize: "vertical", border: `1px solid ${T.borderStrong}`, borderRadius: T.radiusBtn, padding: "8px 10px", font: `400 13px/19px ${T.fontSans}`, color: T.ink, outline: "none", background: T.blue50 }}
              />
              <div style={{ ...label, margin: "14px 0 6px" }}>
                Preview — {count} row{count === 1 ? "" : "s"} affected in "{field}"
              </div>
              <div style={{ border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusBtn, overflow: "hidden" }}>
                {preview.map(([before, after], i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 28px 1fr", alignItems: "center", padding: "6px 10px", borderBottom: i < preview.length - 1 ? `1px solid ${T.borderSubtle}` : "none", background: i % 2 === 1 ? T.bg : T.white }}>
                    <span style={{ font: `400 12px ${T.fontData}`, color: T.inkMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{before}</span>
                    <i className="fa-solid fa-arrow-right-long" style={{ fontSize: 10, color: T.inkSoft, textAlign: "center" }} />
                    <span style={{ font: `500 12px ${T.fontData}`, color: before === after ? T.inkSoft : T.greenDark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{after}{before === after ? "  (no change)" : ""}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: "12px 18px", borderTop: `1px solid ${T.borderSubtle}`, display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }} />
              <Button variant="neutral" onClick={onClose}>Cancel</Button>
              <Button
                variant="primary"
                icon="check"
                onClick={() => ruleText.trim() && count > 0 && onApply({ text: ruleText.trim(), field, fn: fnRef, count })}
                style={ruleText.trim() && count > 0 ? {} : { opacity: 0.45, cursor: "default" }}
              >
                Looks right — apply
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
